'use client'

import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { type Address } from 'viem'
import { useAccount, useChainId } from 'wagmi'

import { assets } from '@/ui/assets'
import { Button } from '@/ui/atoms/button/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/ui/atoms/dropdown/DropdownMenu'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Tooltip, TooltipContentShort, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'

import { ArrowLeft, Check, ChevronDown, DollarSign, Info, Plus, TrendingUp } from 'lucide-react'
import { ActivateConfirmationModal, type ConfigSummary } from './ActivateConfirmationModal'
import { CollateralAssetsSection } from './CollateralAssetsSection'
import { DelegationApprovalBox } from './DelegationApprovalBox'
import { EulerCollateralBreakdownSkeleton } from './EulerCollateralBreakdownSkeleton'
import { EulerCollateralSection } from './EulerCollateralSection'

import { DEFAULT_APPROVAL_LIMIT } from '@/config/card'
import { EVC_ADDRESS } from '@/config/euler-contracts'
import { useAccountLiquidity } from '@/domain/euler/positions/useAccountLiquidity'
import { ELendPosition } from '@/domain/euler/positions/useELendPositions'
import { useEnabledCollateralVaults } from '@/domain/euler/positions/useEnabledCollateralVaults'
import { useLtvWeightedCollateral } from '../hooks/useLtvWeightedCollateral'
import { getSubAccountAddress } from '@/domain/euler/shared/subAccounts'
import { useOpenDialog } from '@/domain/hooks/useDialog'
import { UserPosition } from '@/domain/market-info/marketInfo'
import { useCardSolverConfigs } from '@/domain/state'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { eModeDialogConfig } from '@/features/dialogs/e-mode/EModeDialog'
import { eAddToPositionDialogConfig } from '@/features/eborrow-details/dialogs/add-to-position/EAddToPositionDialog'
import { eCollateralStatusDialogConfig } from '@/features/eborrow-details/dialogs/collateral-status/ECollateralStatusDialog'
import { eBorrowUnifiedRepayDialogConfig } from '@/features/eborrow-details/dialogs/repay/EBorrowUnifiedRepayDialog'
import { getTicks } from '@/features/my-portfolio/logic/position'
import { PositionSummary } from '@/features/my-portfolio/logic/types'
import { getTokenImage } from '@/ui/assets/tokens'
import Image from 'next/image'
import { repayDialogConfig } from '../../dialogs/repay/RepayDialog'
import {
  BorrowTokenOption,
  CREDIT_TYPES,
  CardMode,
  EULER_TOKENS,
  type EulerVaultInfo,
  HYPURRFI_TOKENS,
  type ProtocolConfig,
  ProtocolId,
} from '../constants'
import { useSelectedCard } from '../contexts/SelectedCardContext'
import { useVaultClassifier } from '../contexts/VaultClassifierContext'
import { useATokenApproval } from '../hooks/useATokenApproval'
import { type MergedPosition, useAllEulerPositions } from '../hooks/useAllEulerPositions'
import { useCreditProvider } from '../hooks/useCreditProvider'
import { useDelegation } from '../hooks/useDelegation'
import { useEulerCardData } from '../hooks/useEulerCardData'
import { useEulerDelegation } from '../hooks/useEulerDelegation'
import { useEulerPositionCheck } from '../hooks/useEulerPositionCheck'
import { useHypurrFiCardData } from '../hooks/useHypurrFiCardData'
import { usePooledBorrowEModeMismatch } from '../hooks/usePooledBorrowEModeMismatch'
import { useSolverOptions } from '../hooks/useSolverOptions'
import { CardWarningPanel } from './CardWarningPanel'
import { UnsupportedDebtWarning } from './UnsupportedDebtWarning'
import {
  getActiveEulerCluster,
  getCurrentConfigMarketLabel,
  getEulerCollateralState,
  getLiveCardMarket,
  getLiveCardMode,
  getPooledDebtTokenAddress,
  getRequiredConfiguredSelectionError,
  getSpendCollateralEmptyStateMessage,
  getSpendCollateralSupportedIconDisplay,
  getUnsupportedDebtCheckSubAccountId,
  resolveConfiguredBorrowToken,
  resolveConfiguredEulerVault,
  resolveStoredEulerMarketSelection,
  shouldAutoSelectNewEulerPosition,
} from './cardConfiguration.utils'

interface CardConfigurationProps {
  onBack: () => void
}

export function CardConfiguration({ onBack }: CardConfigurationProps) {
  const chainId = useChainId()
  const { address: userAddress } = useAccount()
  const queryClient = useQueryClient()
  const router = useRouter()
  const openDialog = useOpenDialog()
  const { upsertCardSolverConfig } = useCardSolverConfigs()
  const {
    eulerBorrowVaults,
    eulerWithdrawVaults,
    hasEulerSpendMode,
    hypurrFiBorrowTokens,
    primeEulerVaults,
    yieldEulerVaults,
    hypurrFiWithdrawTokens,
    hypurrFiDebtTokenByBorrowToken,
    isLoading: isSolverLoading,
  } = useSolverOptions()

  const { selectedCard: activeCard } = useSelectedCard()
  const { isPrime: isPrimeVault, isYield: isYieldVault, resolveMarket } = useVaultClassifier()
  const activeCardIsEuler =
    activeCard?.solverType === CREDIT_TYPES.EULER_BORROW || activeCard?.solverType === CREDIT_TYPES.EULER_SPEND
  const activeCardUsesHypurrFi =
    activeCard?.solverType === CREDIT_TYPES.HYPURRFI_BORROW || activeCard?.solverType === CREDIT_TYPES.HYPURRFI_SPEND

  const [mode, setMode] = useState<CardMode>(() => {
    if (!activeCard?.solverType) return 'borrow'
    return activeCard.solverType === CREDIT_TYPES.HYPURRFI_SPEND || activeCard.solverType === CREDIT_TYPES.EULER_SPEND
      ? 'spend'
      : 'borrow'
  })

  const [selectedMarket, setSelectedMarket] = useState<ProtocolId>(() => {
    if (!activeCard?.solverType) return 'hypurrFi'
    return activeCardIsEuler ? 'euler' : 'hypurrFi'
  })

  // The actual Euler vault address lives in solverConfig.borrowVaultAddress, NOT solverCreditProviderAddress
  const activeCardVaultAddress =
    (activeCard?.solverConfig?.borrowVaultAddress as string) || activeCard?.solverCreditProviderAddress || ''

  const activeCardCluster = getActiveEulerCluster({
    activeCardIsEuler,
    activeCardVaultAddress,
    isPrimeVault,
  })
  const liveCardMarket = getLiveCardMarket({
    solverType: activeCard?.solverType,
    activeCardCluster,
  })

  const [eulerCluster, setEulerCluster] = useState<'prime' | 'yield'>(() => {
    if (activeCardCluster) {
      return activeCardCluster
    }
    return 'yield'
  })
  const cardSubAccountId = activeCard?.solverConfig?.subAccountId as number | undefined
  const initialConfiguredPooledBorrowTokenAddress =
    ((activeCard?.solverConfig?.borrowTokenAddress as string | undefined) ||
      (activeCard?.solverConfig?.baseTokenAddress as string | undefined)) ??
    null
  const [storedPooledBorrowTokenAddress, setStoredPooledBorrowTokenAddress] = useState<string | null>(
    initialConfiguredPooledBorrowTokenAddress,
  )
  const [storedEulerSelections, setStoredEulerSelections] = useState<
    Record<
      'prime' | 'yield',
      { subAccountId: number; isNewPosition: boolean; selectedVaultAddress: string | null } | null
    >
  >(() => ({
    prime:
      activeCardCluster === 'prime' && activeCardIsEuler
        ? {
            subAccountId: cardSubAccountId ?? 0,
            isNewPosition: false,
            selectedVaultAddress: activeCardVaultAddress || null,
          }
        : null,
    yield:
      activeCardCluster === 'yield' && activeCardIsEuler
        ? {
            subAccountId: cardSubAccountId ?? 0,
            isNewPosition: false,
            selectedVaultAddress: activeCardVaultAddress || null,
          }
        : null,
  }))

  const [eulerSubAccountId, setEulerSubAccountId] = useState<number>(cardSubAccountId ?? 0)
  const [isNewEulerPosition, setIsNewEulerPosition] = useState(false)
  const unsupportedDebtCheckSubAccountId = getUnsupportedDebtCheckSubAccountId({
    selectedMarket,
    isNewEulerPosition,
    selectedEulerSubAccountId: eulerSubAccountId,
  })

  const { hasUnsupportedDebt } = useEulerPositionCheck({
    subAccountId: unsupportedDebtCheckSubAccountId,
    isEuler: selectedMarket === 'euler',
  })

  const activeEulerVaults = eulerCluster === 'prime' ? primeEulerVaults : yieldEulerVaults
  const allEulerVaults = useMemo(() => [...primeEulerVaults, ...yieldEulerVaults], [primeEulerVaults, yieldEulerVaults])
  const visibleEulerVaults = isSolverLoading ? [] : activeEulerVaults
  const visibleBorrowTokens = isSolverLoading ? [] : hypurrFiBorrowTokens

  const { positions: allEulerPositions, isLoading: isPositionsLoading } = useAllEulerPositions({
    enabled: selectedMarket === 'euler',
  })

  const activeEulerVaultSet = useMemo(
    () => new Set(activeEulerVaults.map((vault) => vault.address.toLowerCase())),
    [activeEulerVaults],
  )

  const relevantEulerPositions = useMemo(() => {
    // A position is relevant to the current market tab (Prime / Yield) when
    // it has SOMETHING actionable in that cluster - i.e. either:
    //   1. debt in one of this cluster's borrow vaults, OR
    //   2. at least one deposit (collateral or savings) in a vault belonging
    //      to this cluster's market.
    // Cross-market positions (e.g. Prime debt + Yield deposits) match #1 on
    // Prime and #2 on Yield, so they appear in both tabs - the user can act
    // on the debt on one side and the deposits on the other.
    const filtered = allEulerPositions.filter((pos) => {
      // Drop positions whose debt is in a vault the card solver doesn't
      // support - the user can't activate a card against them anyway, so
      // they'd only clutter the picker.
      if (pos.hasUnsupportedDebt) return false

      const hasClusterDebt = pos.debtVaults.some((debt) =>
        activeEulerVaultSet.has(debt.liabilityVaultAddress.toLowerCase()),
      )
      if (hasClusterDebt) return true
      // Once a position already has debt, its borrow token/controller is fixed to
      // that debt market. Keep cross-market appearances only for collateral-only
      // positions that still need a controller enabled.
      if (pos.debtVaults.length > 0) return false
      // Market keys use their full "hypurrfi-prime" / "hypurrfi-yield" form,
      // but the tab state stores them as "prime" / "yield" - use the
      // isPrimeVault / isYieldVault helpers to bridge the two.
      const hasClusterDeposit = pos.collateral.some((c) =>
        eulerCluster === 'prime' ? isPrimeVault(c.vaultAddress) : isYieldVault(c.vaultAddress),
      )
      if (hasClusterDeposit) return true
      // Collateral-only position without any deposits in this cluster has
      // nothing to do here - the user has to manage it from the other tab.
      return false
    })
    return filtered.sort((a, b) => {
      const aUSD = a.collateral.reduce((sum, c) => sum + c.depositAmountUSD, 0)
      const bUSD = b.collateral.reduce((sum, c) => sum + c.depositAmountUSD, 0)
      return bUSD - aUSD
    })
  }, [allEulerPositions, activeEulerVaultSet, eulerCluster, isPrimeVault, isYieldVault])

  const nextEulerSubAccountId = useMemo(() => {
    if (allEulerPositions.length === 0) return 0
    return Math.max(...allEulerPositions.map((p) => p.subAccountId)) + 1
  }, [allEulerPositions])

  const [selectedEulerVault, setSelectedEulerVault] = useState<EulerVaultInfo | null>(() => {
    if (activeCardIsEuler && activeCardVaultAddress) {
      return allEulerVaults.find((v) => v.address.toLowerCase() === activeCardVaultAddress.toLowerCase()) ?? null
    }
    return null
  })

  const [selectedBorrowToken, setSelectedBorrowToken] = useState<BorrowTokenOption | null>(() => {
    if (activeCard?.solverType === 'hypurrFi') {
      const configAddr = (activeCard?.solverConfig?.borrowTokenAddress as string)?.toLowerCase()
      if (configAddr) {
        return hypurrFiBorrowTokens.find((t) => t.address.toLowerCase() === configAddr) ?? null
      }
      return null
    }
    return null
  })
  const restorePooledSelection = () => {
    setSelectedBorrowToken(
      resolveConfiguredBorrowToken({
        selectedBorrowToken: null,
        availableBorrowTokens: hypurrFiBorrowTokens,
        configuredBorrowTokenAddress: storedPooledBorrowTokenAddress ?? undefined,
      }),
    )
  }
  const restoreEulerSelection = (cluster: 'prime' | 'yield') => {
    const clusterVaults = cluster === 'prime' ? primeEulerVaults : yieldEulerVaults
    const restoredSelection = resolveStoredEulerMarketSelection({
      savedSelection: storedEulerSelections[cluster],
      availableEulerVaults: clusterVaults,
    })

    if (restoredSelection) {
      setIsNewEulerPosition(restoredSelection.isNewPosition)
      setEulerSubAccountId(restoredSelection.subAccountId)
      setSelectedEulerVault(restoredSelection.selectedVault)
      return
    }

    setIsNewEulerPosition(false)
    setSelectedEulerVault(null)
  }
  const saveCurrentMarketSelection = () => {
    if (selectedMarket === 'euler') {
      setStoredEulerSelections((prev) => ({
        ...prev,
        [eulerCluster]: {
          subAccountId: eulerSubAccountId,
          isNewPosition: isNewEulerPosition,
          selectedVaultAddress: selectedEulerVault?.address ?? null,
        },
      }))
      return
    }

    setStoredPooledBorrowTokenAddress(selectedBorrowToken?.address ?? initialConfiguredPooledBorrowTokenAddress)
  }

  const supportedMarkets = useMemo<ProtocolConfig[]>(
    () => [
      {
        id: 'hypurrFi',
        displayName: 'Pooled',
        icon: assets.hypurrLogo,
        enabled: true,
        borrowCreditType: CREDIT_TYPES.HYPURRFI_BORROW,
        spendCreditType: CREDIT_TYPES.HYPURRFI_SPEND,
        chainId,
        borrowTokenAddress: hypurrFiBorrowTokens[0]?.address ?? HYPURRFI_TOKENS.USDT,
      },
      {
        id: 'euler',
        displayName: eulerCluster === 'prime' ? 'Prime' : 'Yield',
        icon: 'https://app.euler.finance/logo.svg',
        enabled: true,
        borrowCreditType: CREDIT_TYPES.EULER_BORROW,
        spendCreditType: hasEulerSpendMode ? CREDIT_TYPES.EULER_SPEND : undefined,
        chainId,
        borrowTokenAddress: EULER_TOKENS.USDC,
        borrowVaultAddress: eulerBorrowVaults[0]?.address ?? '',
      },
    ],
    [chainId, hypurrFiBorrowTokens, eulerCluster, eulerBorrowVaults, hasEulerSpendMode],
  )

  const currentMarket = supportedMarkets.find((market) => market.id === selectedMarket) || supportedMarkets[0]!

  const activeCardBorrowTokenAddress = ((activeCard?.solverConfig?.borrowTokenAddress as string | undefined) ||
    (activeCard?.solverConfig?.baseTokenAddress as string | undefined)) as Address | undefined

  const effectiveEulerVault = resolveConfiguredEulerVault({
    selectedEulerVault,
    availableEulerVaults: activeEulerVaults,
    configuredVaultAddress: activeCardVaultAddress,
  })
  const effectiveBorrowToken = resolveConfiguredBorrowToken({
    selectedBorrowToken,
    availableBorrowTokens: hypurrFiBorrowTokens,
    configuredBorrowTokenAddress: activeCardBorrowTokenAddress,
  })
  const positionDebtVault =
    selectedMarket === 'euler' && !isNewEulerPosition
      ? relevantEulerPositions.find((p) => p.subAccountId === eulerSubAccountId)?.debtVaults?.[0]
      : undefined
  const isBorrowTokenFixedByPosition = selectedMarket === 'euler' && !isNewEulerPosition && !!positionDebtVault
  const fixedDebtVault = positionDebtVault
  const fixedBorrowTokenSymbol = fixedDebtVault?.stableSymbol
  const fixedEulerVault = fixedDebtVault
    ? allEulerVaults.find((v) => v.address.toLowerCase() === fixedDebtVault.liabilityVaultAddress.toLowerCase())
    : undefined

  const borrowVaultAddress =
    selectedMarket === 'euler'
      ? ((fixedEulerVault?.address ??
          positionDebtVault?.liabilityVaultAddress ??
          effectiveEulerVault?.address) as Address)
      : (currentMarket.borrowVaultAddress as Address | undefined)
  const borrowTokenAddress =
    selectedMarket === 'euler'
      ? ((fixedEulerVault?.borrowToken ?? effectiveEulerVault?.borrowToken) as Address)
      : (effectiveBorrowToken?.address as Address | undefined)
  const pooledDebtTokenAddress = getPooledDebtTokenAddress({
    isPooledMarket: selectedMarket === 'hypurrFi',
    borrowTokenAddress,
    hypurrFiDebtTokenByBorrowToken,
  }) as Address | undefined

  const hypurrfiData = useHypurrFiCardData({
    chainId,
    borrowTokenAddress,
    enabled: selectedMarket === 'hypurrFi' && (mode === 'spend' || !!borrowTokenAddress),
  })

  const {
    message: pooledBorrowEModeMismatchMessage,
    hasMismatch: hasPooledBorrowEModeMismatch,
    userEModeCategoryId: pooledBorrowUserEModeCategoryId,
  } = usePooledBorrowEModeMismatch({
    marketDataSource: selectedMarket === 'hypurrFi' ? 'hypurrFi' : 'euler',
    mode,
    borrowTokenAddress,
    marketInfo: hypurrfiData.marketInfo,
  })

  const activeHypurrfiData = useHypurrFiCardData({
    chainId,
    borrowTokenAddress: activeCardBorrowTokenAddress,
    enabled: activeCardUsesHypurrFi && !!activeCardBorrowTokenAddress,
  })

  const eulerData = useEulerCardData({
    chainId: currentMarket.chainId,
    borrowVaultAddress,
    userAddress,
    enabled: selectedMarket === 'euler',
    subAccountId: eulerSubAccountId,
  })

  const activeEulerData = useEulerCardData({
    chainId,
    borrowVaultAddress: activeCardIsEuler ? (activeCardVaultAddress as Address) : undefined,
    userAddress,
    enabled: activeCardIsEuler && !!activeCardVaultAddress,
    subAccountId: cardSubAccountId,
  })

  const {
    collateralPositions,
    spendModeCollateralPositions: rawSpendModeCollateralPositions,
    totalBorrowUSD,
    borrowTokenSymbol,
    borrowAPY,
    healthFactor,
    totalCollateralUSD: marketCollateralUSD,
    maxBorrowCapacityUSD: marketMaxBorrowCapacityUSD,
  } = selectedMarket === 'euler' ? eulerData : hypurrfiData

  // Set of vault addresses (lowercased) that are enabled as collateral on the
  // configured Euler sub-account. Drives the Collateral vs Savings split below.
  const enabledCollateralVaultSet = useMemo(() => {
    if (selectedMarket !== 'euler') return new Set<string>()
    return new Set((eulerData.enabledCollateralVaults ?? []).map((a) => a.toLowerCase()))
  }, [selectedMarket, eulerData.enabledCollateralVaults])

  // Classification matches the dashboard's Sub-account Positions view (see
  // `enabledCollaterals` / `savingsCollaterals` in `SubAccountPositions.tsx`).
  // A deposit only counts as "Enabled Collateral" when BOTH are true:
  //   1. It is enabled via EVC.getCollaterals for this sub-account.
  //   2. Its market matches the controller (the selected borrow vault's market).
  // Enabled-but-different-market deposits (e.g. Yield collateral on a Prime
  // controller) are shown under Savings because they don't back the debt.
  const controllerMarketKey = useMemo(() => {
    if (selectedMarket !== 'euler') return undefined
    if (!borrowVaultAddress) return undefined
    return resolveMarket(borrowVaultAddress)?.key
  }, [selectedMarket, borrowVaultAddress, resolveMarket])

  const { enabledCollateralPositions, savingsPositions } = useMemo(() => {
    if (selectedMarket !== 'euler') {
      return { enabledCollateralPositions: [] as ELendPosition[], savingsPositions: [] as ELendPosition[] }
    }
    const enabled: ELendPosition[] = []
    const savings: ELendPosition[] = []
    for (const pos of collateralPositions as ELendPosition[]) {
      const state = getEulerCollateralState({
        collateral: [{ vaultAddress: pos.vaultAddress, depositAmountUSD: Number(pos.depositAmountUSD) }],
        enabledVaults: [...enabledCollateralVaultSet],
        controllerMarketKey,
        resolveMarket,
      })

      if (state.hasEnabledCollateral) {
        enabled.push(pos)
      } else {
        savings.push(pos)
      }
    }
    return { enabledCollateralPositions: enabled, savingsPositions: savings }
  }, [selectedMarket, collateralPositions, enabledCollateralVaultSet, controllerMarketKey, resolveMarket])

  // The card's borrow vault belongs to a specific market (Prime or Yield). A
  // savings deposit in a different market can be "enabled" on-chain but won't
  // back this controller, so the in-card Enable action would not actually
  // improve spending power. We gate the Enable button for those rows and
  // explain why via tooltip, matching the dashboard's mental model.
  const controllerMarketName = useMemo(() => {
    if (selectedMarket !== 'euler') return undefined
    if (!borrowVaultAddress) return undefined
    return resolveMarket(borrowVaultAddress)?.name
  }, [selectedMarket, borrowVaultAddress, resolveMarket])

  const enableDisabledByVault = useMemo(() => {
    const map = new Map<string, string>()
    if (selectedMarket !== 'euler' || !controllerMarketKey || !controllerMarketName) return map
    for (const pos of savingsPositions) {
      const vaultMarket = resolveMarket(pos.vaultAddress as Address)
      if (vaultMarket?.key && vaultMarket.key !== controllerMarketKey) {
        map.set(
          pos.vaultAddress.toLowerCase(),
          `This deposit is in the ${vaultMarket.name} market, but this card borrows from the ${controllerMarketName} market. Enabling it here won't add to this card's spending power - switch the card's market or deposit into ${controllerMarketName} first.`,
        )
      }
    }
    return map
  }, [selectedMarket, savingsPositions, controllerMarketKey, controllerMarketName, resolveMarket])

  const handleEnableAsCollateral = useCallback(
    (position: ELendPosition) => {
      if (selectedMarket !== 'euler') return
      const depositAmount = Number(position.depositAmount)
      const depositUSD = Number(position.depositAmountUSD)
      const collateralPriceUSD = depositAmount > 0 ? depositUSD / depositAmount : 0

      openDialog(eCollateralStatusDialogConfig as any, {
        collateralVault: position.vaultAddress,
        collateralAsset: position.token.address,
        collateralSymbol: position.token.symbol,
        collateralDecimals: position.token.decimals,
        collateralLogoURI: position.token.logoURI,
        currentCollateralFormatted: depositAmount,
        collateralPriceUSD,
        subAccountId: position.subAccountId,
        evcAddress: EVC_ADDRESS,
        action: 'enable',
        currentDebtUSD: eulerData.totalDebtValueUSD ?? 0,
        accountTotalCollateralUSD: eulerData.totalCollateralUSD ?? 0,
        liquidationLTV: eulerData.effectiveLiquidationLTV ?? 0,
        currentLTV: eulerData.currentLTV ?? 0,
      })
    },
    [
      selectedMarket,
      openDialog,
      eulerData.totalDebtValueUSD,
      eulerData.totalCollateralUSD,
      eulerData.effectiveLiquidationLTV,
      eulerData.currentLTV,
    ],
  )

  const spendModeCollateralPositions = useMemo(() => {
    if (selectedMarket !== 'euler') {
      return [
        ...(rawSpendModeCollateralPositions as (UserPosition & {
          spendingPowerUSD?: import('bignumber.js').default
        })[]),
      ].sort((a, b) => b.spendingPowerUSD?.minus(a.spendingPowerUSD ?? 0).toNumber() ?? 0)
    }
    return (rawSpendModeCollateralPositions as (ELendPosition & { spendingPowerUSD: import('bignumber.js').default })[])
      .filter((position) => {
        const prime = isPrimeVault(position.vaultAddress)
        return eulerCluster === 'prime' ? prime : !prime
      })
      .sort((a, b) => b.spendingPowerUSD.minus(a.spendingPowerUSD).toNumber())
  }, [rawSpendModeCollateralPositions, selectedMarket, eulerCluster, isPrimeVault])

  const supportedSpendCollateralLabels = useMemo(() => {
    if (selectedMarket === 'euler') {
      const clusterVaultAddresses = new Set(activeEulerVaults.map((vault) => vault.address.toLowerCase()))
      const labels = eulerWithdrawVaults
        .filter((vault) => clusterVaultAddresses.has(vault.address.toLowerCase()))
        .map((vault) => vault.label)
      return [...new Set(labels)]
    }

    return [...new Set(hypurrFiWithdrawTokens.map((token) => token.symbol))]
  }, [selectedMarket, activeEulerVaults, eulerWithdrawVaults, hypurrFiWithdrawTokens])
  const spendCollateralIconDisplay = getSpendCollateralSupportedIconDisplay(supportedSpendCollateralLabels)

  // For Euler, "Total deposited" on the card reflects deposits in the same
  // market cluster as the card's borrow vault (resolved via `resolveMarket`).
  // Cross-market deposits are listed under Savings and don't count here.
  // Vaults the classifier can't resolve have an unknown market: they land in
  // Savings (see split above) and are excluded from this headline total so we
  // don't treat them as the same cluster without a resolved key.
  // We also can't rely on `accountLens.liquidityInfo.collateralValueRaw` as
  // the source of truth: it returns 0 when the sub-account has no controller
  // (queryFailure), which would show "$0.00" for collateral-only positions.
  const totalCollateralUSD = useMemo(() => {
    if (selectedMarket !== 'euler') return marketCollateralUSD
    return (collateralPositions as ELendPosition[]).reduce((sum, pos) => {
      if (controllerMarketKey) {
        const vaultMarketKey = resolveMarket(pos.vaultAddress as Address)?.key
        if (vaultMarketKey && vaultMarketKey !== controllerMarketKey) return sum
        if (!vaultMarketKey) return sum
      }
      return sum + Number(pos.depositAmountUSD)
    }, 0)
  }, [selectedMarket, marketCollateralUSD, collateralPositions, controllerMarketKey, resolveMarket])
  const maxBorrowCapacityUSD = marketMaxBorrowCapacityUSD || 0
  const borrowedUSD = selectedMarket === 'euler' ? eulerData?.totalDebtValueUSD ?? 0 : totalBorrowUSD.toNumber()

  const [selectedAsset, setSelectedAsset] = useState<UserPosition | ELendPosition | null>(null)
  const [selectError, setSelectError] = useState<string>('')
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [activatedEulerSubAccountId, setActivatedEulerSubAccountId] = useState<number | null>(null)

  const marketForReset = selectedMarket
  useEffect(() => {
    void marketForReset
    setSelectedAsset(null)
    setSelectError('')
  }, [marketForReset])

  useEffect(() => {
    if (mode !== 'spend' || selectedAsset) return

    if (selectedMarket === 'euler') {
      const activeSupplyVault = (
        (activeCard?.solverConfig?.supplyVaultAddress as string) ||
        (activeCard?.solverConfig?.borrowVaultAddress as string)
      )?.toLowerCase()
      const activeSubAccountId = activeCard?.solverConfig?.subAccountId as number | undefined
      if (!activeSupplyVault) return
      const match = (spendModeCollateralPositions as ELendPosition[]).find(
        (p) =>
          p.vaultAddress.toLowerCase() === activeSupplyVault &&
          (activeSubAccountId == null || p.subAccountId === activeSubAccountId),
      )
      if (match) setSelectedAsset(match)
    } else {
      const activeAToken = (activeCard?.solverConfig?.aTokenAddress as string)?.toLowerCase()
      if (!activeAToken) return
      const match = (spendModeCollateralPositions as UserPosition[]).find(
        (p) => p.reserve.aToken.address.toLowerCase() === activeAToken,
      )
      if (match) setSelectedAsset(match)
    }
  }, [
    mode,
    selectedAsset,
    selectedMarket,
    spendModeCollateralPositions,
    activeCard?.solverConfig?.supplyVaultAddress,
    activeCard?.solverConfig?.borrowVaultAddress,
    activeCard?.solverConfig?.subAccountId,
    activeCard?.solverConfig?.aTokenAddress,
  ])

  useEffect(() => {
    if (selectedMarket !== 'euler') return
    if (isNewEulerPosition) {
      if (eulerSubAccountId !== nextEulerSubAccountId) {
        setEulerSubAccountId(nextEulerSubAccountId)
      }
      return
    }

    if (
      shouldAutoSelectNewEulerPosition({
        selectedMarket,
        isNewEulerPosition,
        isPositionsLoading,
        eulerSubAccountId,
        relevantEulerPositions,
      })
    ) {
      setIsNewEulerPosition(true)
      setEulerSubAccountId(nextEulerSubAccountId)
    }
  }, [
    selectedMarket,
    isNewEulerPosition,
    isPositionsLoading,
    eulerSubAccountId,
    nextEulerSubAccountId,
    relevantEulerPositions,
  ])

  const creditProviderMutation = useCreditProvider()
  const hypurrfiDelegation = useDelegation({
    debtTokenAddress: pooledDebtTokenAddress,
    borrowTokenAddress,
  })
  const eulerDelegation = useEulerDelegation({
    subAccountId: eulerSubAccountId,
    borrowVaultAddress,
  })
  const delegation = selectedMarket === 'euler' ? eulerDelegation : hypurrfiDelegation

  const aTokenAddressForApproval = useMemo(() => {
    if (!selectedAsset) return undefined
    if (selectedMarket === 'euler' && 'vaultAddress' in selectedAsset) {
      return selectedAsset.vaultAddress as Address
    }
    if (selectedMarket === 'hypurrFi' && 'reserve' in selectedAsset) {
      return selectedAsset.reserve.aToken.address as Address
    }
    return undefined
  }, [selectedAsset, selectedMarket])

  const aTokenApproval = useATokenApproval({
    tokenAddress: aTokenAddressForApproval,
  })

  // For Euler: the actual vault address is in solverConfig.borrowVaultAddress
  // For HypurrFi: the backend stores borrowTokenAddress in solverConfig (if sent during activation).
  const effectiveSubAccountId = isNewEulerPosition ? nextEulerSubAccountId : eulerSubAccountId
  const isActiveEulerBorrowVault =
    activeCard?.solverType === CREDIT_TYPES.EULER_BORROW &&
    eulerCluster === activeCardCluster &&
    effectiveSubAccountId === activeCard?.solverConfig?.subAccountId &&
    (effectiveEulerVault?.address ?? '').toLowerCase() === activeCardVaultAddress.toLowerCase()

  // Broader check used for UI elements shared across borrow/spend (e.g., "Add Collateral" visibility)
  const isActiveEulerVault =
    activeCardIsEuler &&
    eulerCluster === activeCardCluster &&
    effectiveSubAccountId === activeCard?.solverConfig?.subAccountId &&
    (effectiveEulerVault?.address ?? '').toLowerCase() === activeCardVaultAddress.toLowerCase()

  const activeHypurrFiBorrowToken = (activeCard?.solverConfig?.borrowTokenAddress as string)?.toLowerCase()
  const isActiveHypurrFiToken =
    activeCard?.solverType === 'hypurrFi' &&
    // If backend has borrowTokenAddress, check the specific token; otherwise just match solver type
    (!activeHypurrFiBorrowToken || effectiveBorrowToken?.address.toLowerCase() === activeHypurrFiBorrowToken)

  const isActiveBorrowMode = useMemo(() => {
    if (mode !== 'borrow') return false
    if (selectedMarket === 'euler') return isActiveEulerBorrowVault
    if (selectedMarket === 'hypurrFi') return isActiveHypurrFiToken
    return false
  }, [mode, selectedMarket, isActiveEulerBorrowVault, isActiveHypurrFiToken])

  const isActiveSpendMode = useMemo(() => {
    if (mode !== 'spend') return false
    if (!selectedAsset) return false

    if (selectedMarket === 'euler') {
      if (activeCard?.solverType !== CREDIT_TYPES.EULER_SPEND) return false
      const activeSupplyVault = (
        (activeCard?.solverConfig?.supplyVaultAddress as string) ||
        (activeCard?.solverConfig?.borrowVaultAddress as string)
      )?.toLowerCase()
      const activeSubAccountId = activeCard?.solverConfig?.subAccountId as number | undefined
      if (!activeSupplyVault) return false
      if (!('vaultAddress' in selectedAsset)) return false
      return (
        (selectedAsset as ELendPosition).vaultAddress.toLowerCase() === activeSupplyVault &&
        (activeSubAccountId == null || (selectedAsset as ELendPosition).subAccountId === activeSubAccountId)
      )
    }

    if (selectedMarket === 'hypurrFi') {
      if (activeCard?.solverType !== CREDIT_TYPES.HYPURRFI_SPEND) return false
      const activeAToken = (activeCard?.solverConfig?.aTokenAddress as string)?.toLowerCase()
      if (!activeAToken) return true
      const selectedAToken = 'reserve' in selectedAsset ? selectedAsset.reserve.aToken.address.toLowerCase() : ''
      return activeAToken === selectedAToken
    }

    return false
  }, [
    mode,
    selectedMarket,
    activeCard?.solverType,
    activeCard?.solverConfig?.supplyVaultAddress,
    activeCard?.solverConfig?.borrowVaultAddress,
    activeCard?.solverConfig?.subAccountId,
    activeCard?.solverConfig?.aTokenAddress,
    selectedAsset,
  ])

  const isCurrentConfigActive = isActiveBorrowMode || isActiveSpendMode
  const liveCardMode = activeCard?.solverType ? getLiveCardMode(activeCard.solverType) : null

  const positionSummary: PositionSummary | undefined = useMemo(() => {
    if (maxBorrowCapacityUSD === 0) return undefined

    const visualizationScale = selectedMarket === 'euler' ? maxBorrowCapacityUSD * 1.5 : totalCollateralUSD
    if (visualizationScale === 0) return undefined

    const borrowPercent = (borrowedUSD / visualizationScale) * 100
    const maxPercent = (maxBorrowCapacityUSD / visualizationScale) * 100
    const restPercent = 100 - borrowPercent

    return {
      borrow: {
        current: NormalizedUnitNumber(borrowedUSD),
        max: NormalizedUnitNumber(maxBorrowCapacityUSD),
        percents: {
          borrowed: borrowPercent,
          max: maxPercent,
          rest: restPercent,
        },
      },
      totalCollateralUSD: NormalizedUnitNumber(visualizationScale),
      hasCollaterals: visualizationScale > 0,
      hasDeposits: visualizationScale > 0,
      healthFactor: NormalizedUnitNumber(0),
      collaterals: [],
    } as PositionSummary
  }, [borrowedUSD, maxBorrowCapacityUSD, totalCollateralUSD, selectedMarket])

  const ticks = getTicks({
    numLabels: 5,
    ticksPerLabel: 2,
    totalCollateralUSD: positionSummary?.totalCollateralUSD,
    xAxisFallbackMax: NormalizedUnitNumber(90_000),
  })

  const handleRepay = () => {
    if (selectedMarket === 'euler') {
      const eulerBorrowPosition = eulerData.primaryBorrowPosition
      const eulerVault = eulerData.eulerVault

      if (eulerBorrowPosition && eulerVault?.asset) {
        openDialog(eBorrowUnifiedRepayDialogConfig, {
          liabilityVault: borrowVaultAddress as Address,
          liabilitySymbol: borrowTokenSymbol || 'USDC',
          liabilityDecimals: eulerVault.assetDecimals || 6,
          liabilityAsset: eulerVault.asset,
          maxRepayAmount: eulerBorrowPosition.borrowAmount.toString(),
          ...(eulerBorrowPosition.liabilityToken.logoURI && {
            liabilityLogoURI: eulerBorrowPosition.liabilityToken.logoURI,
          }),
        })
      }
    } else {
      const hypurrfiBorrowToken = hypurrfiData.borrowToken
      if (hypurrfiBorrowToken) {
        openDialog(repayDialogConfig, { token: hypurrfiBorrowToken })
      }
    }
  }

  const currentConfigSummary: ConfigSummary | null = useMemo(() => {
    if (!activeCard?.solverType) return null

    const currentLiveMode = getLiveCardMode(activeCard.solverType)
    const isCurrentEuler =
      activeCard.solverType === CREDIT_TYPES.EULER_BORROW || activeCard.solverType === CREDIT_TYPES.EULER_SPEND
    const isCurrentSpend = currentLiveMode === 'spend'
    const activeBorrowTokenAddress = (activeCard.solverConfig?.borrowTokenAddress as string | undefined)?.toLowerCase()

    const activeEulerVault = allEulerVaults.find(
      (vault) => vault.address.toLowerCase() === activeCardVaultAddress.toLowerCase(),
    )

    const activePooledBorrowToken =
      (activeBorrowTokenAddress &&
        hypurrFiBorrowTokens.find((token) => token.address.toLowerCase() === activeBorrowTokenAddress)) ||
      undefined

    const currentBorrowToken = isCurrentEuler
      ? activeEulerVault?.borrowTokenSymbol
      : activeBorrowTokenAddress
        ? activePooledBorrowToken?.symbol
        : undefined

    const activeAToken = (activeCard?.solverConfig?.aTokenAddress as string)?.toLowerCase()
    const activeSupplyVault = (activeCard?.solverConfig?.supplyVaultAddress as string)?.toLowerCase()

    let currentSpendToken: string | undefined
    if (isCurrentSpend && activeCard.solverType === CREDIT_TYPES.EULER_SPEND && activeSupplyVault) {
      const eulerSpendPositions = activeEulerData.spendModeCollateralPositions as ELendPosition[]
      currentSpendToken = eulerSpendPositions.find((p) => p.vaultAddress.toLowerCase() === activeSupplyVault)?.token
        .symbol
    } else if (isCurrentSpend && activeAToken) {
      currentSpendToken = (activeHypurrfiData.spendModeCollateralPositions as UserPosition[]).find(
        (position) => position.reserve.aToken.address.toLowerCase() === activeAToken,
      )?.reserve.token.symbol
    }

    return {
      mode: isCurrentSpend ? 'Spend' : 'Borrow',
      market: isCurrentEuler ? getCurrentConfigMarketLabel(liveCardMarket) : 'Pooled',
      positionLabel: isCurrentEuler ? `Position ${activeCard.solverConfig?.subAccountId ?? 0}` : undefined,
      borrowToken: isCurrentSpend ? currentSpendToken : currentBorrowToken,
      borrowAPY: isCurrentSpend ? undefined : isCurrentEuler ? activeEulerData.borrowAPY : activeHypurrfiData.borrowAPY,
    }
  }, [
    activeCard,
    activeCardVaultAddress,
    activeEulerData.borrowAPY,
    activeEulerData.spendModeCollateralPositions,
    activeHypurrfiData.borrowAPY,
    activeHypurrfiData.spendModeCollateralPositions,
    allEulerVaults,
    hypurrFiBorrowTokens,
    liveCardMarket,
  ])

  const newConfigSummary: ConfigSummary = useMemo(() => {
    const marketLabel = selectedMarket === 'euler' ? (eulerCluster === 'prime' ? 'Prime' : 'Yield') : 'Pooled'

    let tokenSymbol: string | undefined
    if (mode === 'spend' && selectedAsset) {
      tokenSymbol =
        selectedMarket === 'euler' && 'token' in selectedAsset
          ? selectedAsset.token.symbol
          : 'reserve' in selectedAsset
            ? selectedAsset.reserve.token.symbol
            : undefined
    } else if (mode === 'borrow') {
      tokenSymbol = selectedMarket === 'euler' ? effectiveEulerVault?.borrowTokenSymbol : effectiveBorrowToken?.symbol
    }

    // Compute spending power for the summary
    let spendingPower: number | undefined
    if (mode === 'spend' && selectedAsset) {
      if ('spendingPowerUSD' in (selectedAsset as any)) {
        spendingPower = Number((selectedAsset as any).spendingPowerUSD)
      } else if ('reserve' in selectedAsset) {
        const pos = selectedAsset as UserPosition
        spendingPower = Number(pos.collateralBalance.multipliedBy(pos.reserve.priceInUSD))
      }
    }

    return {
      mode: mode === 'borrow' ? 'Borrow' : 'Spend',
      market: marketLabel,
      positionLabel:
        selectedMarket === 'euler'
          ? mode === 'spend' && selectedAsset && 'subAccountId' in selectedAsset
            ? `Position ${(selectedAsset as ELendPosition).subAccountId}`
            : isNewEulerPosition
              ? `New Position ${nextEulerSubAccountId}`
              : `Position ${eulerSubAccountId}`
          : undefined,
      borrowToken: tokenSymbol,
      borrowAPY: mode === 'borrow' ? borrowAPY : undefined,
      spendingPower,
    }
  }, [
    selectedMarket,
    eulerCluster,
    mode,
    effectiveEulerVault,
    effectiveBorrowToken,
    isNewEulerPosition,
    nextEulerSubAccountId,
    eulerSubAccountId,
    borrowAPY,
    selectedAsset,
  ])

  const handleRequestActivate = () => {
    if (!activeCard?.cardID || !userAddress) {
      setSelectError('Card or wallet not available')
      return
    }
    const requiredSelectionError = getRequiredConfiguredSelectionError({
      mode,
      market: selectedMarket,
      hasSelectedAsset: !!selectedAsset,
      hasBorrowToken: !!effectiveBorrowToken,
      hasEulerVault: !!effectiveEulerVault,
      isBorrowTokenFixedByPosition,
    })
    if (requiredSelectionError) {
      setSelectError(requiredSelectionError)
      return
    }
    setSelectError('')
    setShowConfirmModal(true)
  }

  const handleActivate = async (): Promise<boolean> => {
    if (!activeCard?.cardID || !userAddress) {
      setSelectError('Card or wallet not available')
      return false
    }

    const requiredSelectionError = getRequiredConfiguredSelectionError({
      mode,
      market: selectedMarket,
      hasSelectedAsset: !!selectedAsset,
      hasBorrowToken: !!effectiveBorrowToken,
      hasEulerVault: !!effectiveEulerVault,
      isBorrowTokenFixedByPosition,
    })
    if (requiredSelectionError) {
      setSelectError(requiredSelectionError)
      return false
    }

    setSelectError('')
    setActivatedEulerSubAccountId(null)

    try {
      if (mode === 'borrow' && selectedMarket === 'euler' && !eulerDelegation.isSetupComplete) {
        if (!eulerDelegation.isAuthorizationLoaded) {
          setSelectError('Checking Euler position setup. Please wait a moment and try again.')
          return false
        }
        await eulerDelegation.approveDelegation()
      } else if (mode === 'borrow' && delegation.needsDelegation) {
        await hypurrfiDelegation.approveDelegation({
          creditLimit: DEFAULT_APPROVAL_LIMIT,
        })
      }

      const creditType = mode === 'spend' ? currentMarket.spendCreditType : currentMarket.borrowCreditType

      if (!creditType) {
        setSelectError('Spend mode is not supported for this market')
        return false
      }

      if (mode === 'spend' && selectedMarket !== 'euler' && aTokenApproval.needsApproval && selectedAsset) {
        if (!borrowTokenAddress) {
          setSelectError('Please select a borrow token first')
          return false
        }
        // Pooled spend: approve aToken. Euler spend uses EVC operator auth instead.
        if ('reserve' in selectedAsset) {
          await aTokenApproval.approveAToken({
            creditType,
            creditLimit: DEFAULT_APPROVAL_LIMIT,
            userParams: {
              aTokenAddress: selectedAsset.reserve.aToken.address,
              purchaseBaseTokenAddress: borrowTokenAddress,
            },
          })
        }
      }

      let cardSolverConfig: any = {}
      if (mode === 'spend' && selectedAsset && selectedMarket === 'euler' && 'vaultAddress' in selectedAsset) {
        cardSolverConfig = {
          borrowVaultAddress: selectedAsset.vaultAddress,
          supplyVaultAddress: selectedAsset.vaultAddress,
          subAccountId: (selectedAsset as ELendPosition).subAccountId,
        }
      } else if (mode === 'spend' && selectedAsset && 'reserve' in selectedAsset) {
        cardSolverConfig = {
          aTokenAddress: selectedAsset.reserve.aToken.address,
        }
      } else if (selectedMarket === 'euler') {
        if (!borrowVaultAddress) {
          setSelectError('Euler vault options are still loading. Please try again.')
          return false
        }

        cardSolverConfig = {
          borrowVaultAddress,
          subAccountId: effectiveSubAccountId,
        }
      } else {
        cardSolverConfig = {
          borrowTokenAddress: effectiveBorrowToken!.address,
        }
      }
      const activatedSubAccountId =
        selectedMarket === 'euler' && isNewEulerPosition && mode !== 'spend' ? effectiveSubAccountId : null

      let creditProviderAddr: string | undefined
      if (selectedMarket === 'euler' && mode === 'spend' && selectedAsset && 'vaultAddress' in selectedAsset) {
        creditProviderAddr = selectedAsset.vaultAddress
      } else if (selectedMarket === 'euler') {
        creditProviderAddr = borrowVaultAddress
      } else {
        creditProviderAddr = userAddress
      }

      if (!creditProviderAddr) {
        setSelectError('Credit provider is not available yet. Please try again.')
        return false
      }

      await creditProviderMutation.mutateAsync({
        chainId: currentMarket.chainId,
        creditType,
        cardID: activeCard.cardID,
        cardSolverConfig,
      })

      if (userAddress) {
        upsertCardSolverConfig(userAddress, {
          cardID: activeCard.cardID,
          solverType: creditType,
          chainID: currentMarket.chainId,
          cardSolverConfig,
          status: activeCard.status,
          label: activeCard.label,
          last4: activeCard.last4,
        })
      }

      if (activatedSubAccountId != null) {
        setActivatedEulerSubAccountId(activatedSubAccountId)
      }

      await queryClient.invalidateQueries({
        queryKey: ['user-cards', userAddress],
      })

      // When activating a new Euler position, keep CardConfiguration mounted
      // so the success modal can render its "Deposit Collateral" step.
      const keepMountedForSuccessModal =
        selectedMarket === 'euler' && isNewEulerPosition && mode !== 'spend'
      if (!keepMountedForSuccessModal) {
        onBack()
      }
      return true
    } catch (err) {
      setSelectError(err instanceof Error ? err.message : 'Failed to update configuration')
      return false
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack} prefixIcon={<ArrowLeft className="h-4 w-4" />}>
          Back
        </Button>
        <div className="flex items-center gap-3">
          <Typography variant="h2" gradient>
            Card Settings
          </Typography>
          <Typography variant="p" className="text-muted-foreground text-sm">
            {activeCard?.label || `∙∙∙∙ ${activeCard?.last4 ?? '????'}`}
          </Typography>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* ── Left Column: Settings ── */}
        <div className="flex flex-col gap-6">
          <Panel.Wrapper className="relative overflow-hidden">
            <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-gradient-yellow/5 via-transparent to-gradient-green/5" />
            <Panel.Content className="relative space-y-6 p-6">
              <div className="flex flex-col gap-4">
                <div>
                  <Typography variant="p" className="mb-2 font-medium text-muted-foreground text-sm">
                    Mode
                  </Typography>
                  <div className="relative flex rounded-lg border border-border bg-muted/30 p-0.5">
                    <div
                      className="absolute top-0.5 bottom-0.5 rounded-md bg-primary/15 transition-all duration-200"
                      style={{
                        left: mode === 'borrow' ? '2px' : '50%',
                        width: 'calc(50% - 4px)',
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setMode('borrow')
                        setSelectError('')
                      }}
                      className={cn(
                        'relative z-10 flex-1 rounded-md px-4 py-1.5 text-center font-medium text-sm transition-colors',
                        mode === 'borrow' ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      <span className="flex items-center justify-center gap-1.5">
                        Borrow
                        {liveCardMode === 'borrow' && (
                          <span className="relative flex h-1.5 w-1.5">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-500" />
                          </span>
                        )}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setMode('spend')
                        setSelectError('')
                      }}
                      className={cn(
                        'relative z-10 flex-1 rounded-md px-4 py-1.5 text-center font-medium text-sm transition-colors',
                        mode === 'spend' ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      <span className="flex items-center justify-center gap-1.5">
                        Spend
                        {liveCardMode === 'spend' && (
                          <span className="relative flex h-1.5 w-1.5">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-500" />
                          </span>
                        )}
                      </span>
                    </button>
                  </div>
                </div>

                <div>
                  <Typography variant="p" className="mb-2 font-medium text-muted-foreground text-sm">
                    Market
                  </Typography>
                  <div className="flex gap-2">
                    <ModeTab
                      label="Pooled"
                      active={selectedMarket === 'hypurrFi'}
                      isLive={liveCardMode === mode && liveCardMarket === 'hypurrFi'}
                      onClick={() => {
                        saveCurrentMarketSelection()
                        setSelectedMarket('hypurrFi')
                        restorePooledSelection()
                        setSelectedAsset(null)
                        setSelectError('')
                      }}
                    />
                    {primeEulerVaults.length > 0 && (
                      <ModeTab
                        label="Prime"
                        active={selectedMarket === 'euler' && eulerCluster === 'prime'}
                        isLive={liveCardMode === mode && liveCardMarket === 'prime'}
                        onClick={() => {
                          saveCurrentMarketSelection()
                          setSelectedMarket('euler')
                          setEulerCluster('prime')
                          restoreEulerSelection('prime')
                          setSelectedAsset(null)
                          setSelectError('')
                        }}
                      />
                    )}
                    {yieldEulerVaults.length > 0 && (
                      <ModeTab
                        label="Yield"
                        active={selectedMarket === 'euler' && eulerCluster === 'yield'}
                        isLive={liveCardMode === mode && liveCardMarket === 'yield'}
                        onClick={() => {
                          saveCurrentMarketSelection()
                          setSelectedMarket('euler')
                          setEulerCluster('yield')
                          restoreEulerSelection('yield')
                          setSelectedAsset(null)
                          setSelectError('')
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Euler position selector — only in borrow mode, spend mode shows flat vault list */}
              {selectedMarket === 'euler' && mode !== 'spend' && (
                <div>
                  <Typography variant="p" className="mb-3 font-medium text-muted-foreground text-sm">
                    Select Position
                  </Typography>
                  {isPositionsLoading && relevantEulerPositions.length === 0 ? (
                    <div className="flex flex-col gap-3">
                      {[0, 1].map((i) => (
                        <div key={i} className="animate-pulse rounded-xl border border-border/50 bg-secondary/20 p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="h-6 w-6 rounded-full bg-muted" />
                              <div className="flex flex-col gap-1">
                                <div className="h-4 w-20 rounded bg-muted" />
                                <div className="h-3 w-16 rounded bg-muted" />
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <div className="h-3 w-20 rounded bg-muted" />
                              <div className="h-4 w-14 rounded bg-muted" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3">
                      {relevantEulerPositions.map((position) => {
                        const isSelected = !isNewEulerPosition && eulerSubAccountId === position.subAccountId
                        const isActivePosition =
                          activeCard?.solverType === CREDIT_TYPES.EULER_BORROW &&
                          activeCard?.solverConfig?.subAccountId === position.subAccountId

                        return (
                          <EulerPositionCard
                            key={position.subAccountId}
                            position={position}
                            isSelected={isSelected}
                            isActivePosition={isActivePosition}
                            fallbackBorrowVaultAddress={effectiveEulerVault?.address as Address | undefined}
                            userAddress={userAddress}
                            chainId={chainId}
                            onClick={() => {
                              setIsNewEulerPosition(false)
                              setEulerSubAccountId(position.subAccountId)
                              if (position.debtVaults.length > 0) {
                                const debtVault = allEulerVaults.find(
                                  (v) =>
                                    v.address.toLowerCase() ===
                                    position.debtVaults[0]!.liabilityVaultAddress.toLowerCase(),
                                )
                                setSelectedEulerVault(debtVault ?? null)
                              }
                            }}
                            onAddCollateral={(liquidity) => {
                              openDialog(eAddToPositionDialogConfig as any, {
                                subAccountId: position.subAccountId,
                                controller:
                                  position.debtVaults.length > 0
                                    ? (position.debtVaults[0]!.liabilityVaultAddress as Address)
                                    : null,
                                currentDebtUSD: liquidity.currentDebtUSD,
                                currentLTV: liquidity.currentLTV,
                                liquidationLTV: liquidity.liquidationLTV,
                              })
                            }}
                          />
                        )
                      })}
                      <button
                        type="button"
                        onClick={() => {
                          setIsNewEulerPosition(true)
                          setEulerSubAccountId(nextEulerSubAccountId)
                        }}
                        className={cn(
                          'group flex w-full items-center justify-center gap-3 rounded-xl border border-dashed p-4 transition-all duration-200',
                          isNewEulerPosition
                            ? 'border-primary/50 bg-primary/5 text-primary shadow-sm ring-1 ring-primary/20'
                            : 'border-border/60 bg-transparent text-muted-foreground hover:border-primary/40 hover:bg-secondary/20 hover:text-foreground',
                        )}
                      >
                        <div
                          className={cn(
                            'flex h-8 w-8 items-center justify-center rounded-full transition-colors',
                            isNewEulerPosition
                              ? 'bg-primary/20'
                              : 'bg-secondary group-hover:bg-primary/10 group-hover:text-primary',
                          )}
                        >
                          <Plus className="h-4 w-4" />
                        </div>
                        <span className="font-medium text-sm">Create New Position (#{nextEulerSubAccountId})</span>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Borrow token / vault selector */}
              {mode === 'borrow' && (
                <div>
                  <div className="mb-3 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-gradient-yellow/20 to-gradient-green/20">
                      <DollarSign className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <Typography variant="h4" className="font-semibold">
                        Borrow Token
                      </Typography>
                      <Typography variant="p" className="text-muted-foreground text-sm">
                        Auto-borrow when you spend with your card
                      </Typography>
                    </div>
                  </div>

                  {isBorrowTokenFixedByPosition ? (
                    <div className="flex w-full items-center justify-between rounded-lg border border-border bg-input px-3 py-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Image
                          src={getTokenImage(
                            TokenSymbol(fixedBorrowTokenSymbol || effectiveEulerVault?.borrowTokenSymbol || ''),
                          )}
                          alt={fixedBorrowTokenSymbol || effectiveEulerVault?.borrowTokenSymbol || ''}
                          width={24}
                          height={24}
                          className="rounded-full"
                        />
                        <span className="font-medium">
                          {fixedBorrowTokenSymbol || effectiveEulerVault?.borrowTokenSymbol}
                        </span>
                        <span className="text-muted-foreground text-xs">(Fixed by Position)</span>
                        {activeCardIsEuler &&
                          effectiveEulerVault?.address.toLowerCase() === activeCardVaultAddress.toLowerCase() && (
                            <div className="h-2 w-2 rounded-full bg-green-400" />
                          )}
                      </div>
                    </div>
                  ) : (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="flex w-full items-center justify-between rounded-lg border border-border bg-input px-3 py-3 text-left text-sm transition-colors hover:border-primary"
                        >
                          {selectedMarket === 'euler' ? (
                            effectiveEulerVault ? (
                              <div className="flex items-center gap-2">
                                <Image
                                  src={getTokenImage(TokenSymbol(effectiveEulerVault.borrowTokenSymbol))}
                                  alt={effectiveEulerVault.borrowTokenSymbol}
                                  width={24}
                                  height={24}
                                  className="rounded-full"
                                />
                                <span className="font-medium">{effectiveEulerVault.borrowTokenSymbol}</span>
                                <span className="text-muted-foreground text-xs">{effectiveEulerVault.label}</span>
                                {activeCardIsEuler &&
                                  effectiveEulerVault.address.toLowerCase() ===
                                    activeCardVaultAddress.toLowerCase() && (
                                    <div className="h-2 w-2 rounded-full bg-green-400" />
                                  )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">Select borrow token</span>
                            )
                          ) : (
                            <div className="flex items-center gap-2">
                              {effectiveBorrowToken ? (
                                <>
                                  <Image
                                    src={getTokenImage(TokenSymbol(effectiveBorrowToken.symbol))}
                                    alt={effectiveBorrowToken.symbol}
                                    width={24}
                                    height={24}
                                    className="rounded-full"
                                  />
                                  <span className="font-medium">{effectiveBorrowToken.symbol}</span>
                                  {activeCard?.solverType === 'hypurrFi' &&
                                    effectiveBorrowToken.address.toLowerCase() ===
                                      (
                                        (activeCard?.solverConfig?.borrowTokenAddress as string) ?? ''
                                      ).toLowerCase() && <div className="h-2 w-2 rounded-full bg-green-400" />}
                                </>
                              ) : (
                                <span className="text-muted-foreground">Select borrow token</span>
                              )}
                            </div>
                          )}
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
                        {selectedMarket === 'euler'
                          ? visibleEulerVaults.map((vault) => {
                              const isSelected = effectiveEulerVault?.address === vault.address
                              const isLive =
                                activeCardIsEuler &&
                                vault.address.toLowerCase() === activeCardVaultAddress.toLowerCase()
                              return (
                                <DropdownMenuItem
                                  key={vault.address}
                                  onSelect={() => setSelectedEulerVault(vault)}
                                  className={cn('flex items-center justify-between', isSelected && 'bg-primary/10')}
                                >
                                  <div className="flex items-center gap-2">
                                    <Image
                                      src={getTokenImage(TokenSymbol(vault.borrowTokenSymbol))}
                                      alt={vault.borrowTokenSymbol}
                                      width={24}
                                      height={24}
                                      className="rounded-full"
                                    />
                                    <div>
                                      <span className="font-medium">{vault.borrowTokenSymbol}</span>
                                      <span className="ml-2 text-muted-foreground text-xs">{vault.label}</span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {isLive && <div className="h-2 w-2 rounded-full bg-green-400" />}
                                    {isSelected && <Check className="h-4 w-4 text-primary" />}
                                  </div>
                                </DropdownMenuItem>
                              )
                            })
                          : visibleBorrowTokens.map((token) => {
                              const isSelected = effectiveBorrowToken?.address === token.address
                              const isLive =
                                activeCard?.solverType === 'hypurrFi' &&
                                token.address.toLowerCase() ===
                                  ((activeCard?.solverConfig?.borrowTokenAddress as string) ?? '').toLowerCase()
                              return (
                                <DropdownMenuItem
                                  key={token.address}
                                  onSelect={() => setSelectedBorrowToken(token)}
                                  className={cn('flex items-center justify-between', isSelected && 'bg-primary/10')}
                                >
                                  <div className="flex items-center gap-2">
                                    <Image
                                      src={getTokenImage(TokenSymbol(token.symbol))}
                                      alt={token.symbol}
                                      width={24}
                                      height={24}
                                      className="rounded-full"
                                    />
                                    <span className="font-medium">{token.symbol}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {isLive && <div className="h-2 w-2 rounded-full bg-green-400" />}
                                    {isSelected && <Check className="h-4 w-4 text-primary" />}
                                  </div>
                                </DropdownMenuItem>
                              )
                            })}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              )}

              {/* Spend mode collateral selector */}
              {mode === 'spend' && (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <Typography variant="p" className="font-medium text-sm">
                      Select asset to spend
                    </Typography>
                    <Typography variant="p" className="text-muted-foreground text-xs">
                      {selectedMarket === 'euler'
                        ? 'Both collateral and savings deposits can be spent'
                        : 'Pick a collateral to spend from'}
                    </Typography>
                  </div>
                  <div className="flex flex-col gap-2">
                    {selectedMarket === 'euler'
                      ? (
                          spendModeCollateralPositions as (ELendPosition & {
                            spendingPowerUSD: import('bignumber.js').default
                            debtUSD: import('bignumber.js').default
                            debtSymbol?: string
                            healthFactor?: number
                          })[]
                        ).map((asset) => {
                          const isSelected =
                            selectedAsset &&
                            (selectedAsset as ELendPosition).vaultAddress === asset.vaultAddress &&
                            (selectedAsset as ELendPosition).subAccountId === asset.subAccountId
                          const activeSpendVault = (
                            (activeCard?.solverConfig?.supplyVaultAddress as string) ||
                            (activeCard?.solverConfig?.borrowVaultAddress as string)
                          )?.toLowerCase()
                          const isActiveSpendAsset =
                            activeCard?.solverType === CREDIT_TYPES.EULER_SPEND &&
                            activeSpendVault === asset.vaultAddress.toLowerCase() &&
                            activeCard?.solverConfig?.subAccountId === asset.subAccountId
                          return (
                            <button
                              key={`${asset.vaultAddress}-${asset.subAccountId}`}
                              type="button"
                              onClick={() => {
                                setSelectedAsset(asset)
                                setSelectError('')
                              }}
                              className={cn(
                                'flex items-center justify-between gap-3 rounded-lg border p-3 text-left transition-all',
                                isSelected
                                  ? 'border-primary/50 bg-primary/5 ring-1 ring-primary/20'
                                  : 'border-border/50 bg-secondary/20 hover:border-primary/30 hover:bg-secondary/40',
                              )}
                            >
                              <div className="flex items-center gap-3">
                                <Image
                                  src={getTokenImage(TokenSymbol(asset.token.symbol))}
                                  alt={asset.token.symbol}
                                  width={32}
                                  height={32}
                                  className="rounded-full"
                                />
                                <div className="flex flex-col">
                                  <div className="flex items-center gap-1.5">
                                    <Typography variant="p" className="font-semibold text-sm leading-tight">
                                      {asset.token.symbol}
                                    </Typography>
                                    {isActiveSpendAsset && (
                                      <div className="flex items-center gap-1 rounded-full bg-green-500/10 px-1.5 py-0.5 font-semibold text-[9px] text-green-400 uppercase tracking-wide">
                                        <span className="relative flex h-1.5 w-1.5">
                                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                                          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-500" />
                                        </span>
                                        Active
                                      </div>
                                    )}
                                  </div>
                                  <span className="text-muted-foreground text-xs leading-tight">
                                    Position {asset.subAccountId}
                                  </span>
                                  {asset.debtUSD.gt(0) && (
                                    <span className="text-[10px] text-muted-foreground/70 leading-tight">
                                      Borrowing ${asset.debtUSD.toFixed(2)} {asset.debtSymbol}
                                      {asset.healthFactor != null && (
                                        <>
                                          {' · '}
                                          <span
                                            className={cn(
                                              asset.healthFactor < 1.1
                                                ? 'text-red-400'
                                                : asset.healthFactor < 1.5
                                                  ? 'text-amber-400'
                                                  : 'text-green-400',
                                            )}
                                          >
                                            HF {asset.healthFactor > 100 ? '>100' : asset.healthFactor.toFixed(2)}
                                          </span>
                                        </>
                                      )}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="text-right">
                                  <div className="text-muted-foreground text-xs">Spending Power</div>
                                  <div className="font-semibold text-foreground text-sm">
                                    ${asset.spendingPowerUSD.toFixed(2)}
                                  </div>
                                </div>
                                {isSelected && (
                                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                                    <Check className="h-3 w-3 stroke-[3]" />
                                  </div>
                                )}
                              </div>
                            </button>
                          )
                        })
                      : (
                          spendModeCollateralPositions as (UserPosition & {
                            spendingPowerUSD?: import('bignumber.js').default
                          })[]
                        ).map((asset) => {
                          const isSelected =
                            selectedAsset &&
                            'reserve' in selectedAsset &&
                            selectedAsset.reserve.token.symbol === asset.reserve.token.symbol
                          const activeAToken = (activeCard?.solverConfig?.aTokenAddress as string)?.toLowerCase()
                          const isActiveSpendAsset =
                            activeCard?.solverType === CREDIT_TYPES.HYPURRFI_SPEND &&
                            !!activeAToken &&
                            asset.reserve.aToken.address.toLowerCase() === activeAToken
                          return (
                            <button
                              key={asset.reserve.token.address}
                              type="button"
                              onClick={() => {
                                setSelectedAsset(asset)
                                setSelectError('')
                              }}
                              className={cn(
                                'flex items-center gap-3 rounded-lg border p-3 text-left transition-all',
                                isSelected
                                  ? 'border-primary/50 bg-primary/5 ring-1 ring-primary/20'
                                  : 'border-border/50 bg-secondary/20 hover:border-primary/30 hover:bg-secondary/40',
                              )}
                            >
                              <Image
                                src={getTokenImage(asset.reserve.token.symbol)}
                                alt={asset.reserve.token.symbol}
                                width={32}
                                height={32}
                                className="rounded-full"
                              />
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-1.5">
                                    <Typography variant="p" className="font-medium text-sm">
                                      {asset.reserve.token.symbol}
                                    </Typography>
                                    {isActiveSpendAsset && (
                                      <div className="flex items-center gap-1 rounded-full bg-green-500/10 px-1.5 py-0.5 font-semibold text-[9px] text-green-400 uppercase tracking-wide">
                                        <span className="relative flex h-1.5 w-1.5">
                                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                                          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-500" />
                                        </span>
                                        Active
                                      </div>
                                    )}
                                  </div>
                                  <Typography variant="p" className="font-semibold text-sm">
                                    {asset.collateralBalance.toFixed(4)}
                                  </Typography>
                                </div>
                                <div className="flex items-center justify-between text-muted-foreground text-xs">
                                  <span>Spending Power</span>
                                  <span>${asset.spendingPowerUSD?.toFixed(2) ?? '0.00'}</span>
                                </div>
                              </div>
                              {isSelected && (
                                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                                  <Check className="h-3 w-3 stroke-[3]" />
                                </div>
                              )}
                            </button>
                          )
                        })}
                    {isSolverLoading ? (
                      <div className="rounded-lg border border-border/50 bg-muted/20 p-6">
                        <div className="flex items-center gap-3 text-muted-foreground text-sm">
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />
                          <span>Loading supported collateral options...</span>
                        </div>
                      </div>
                    ) : (
                      spendModeCollateralPositions.length === 0 && (
                        <div className="rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-4">
                          {spendCollateralIconDisplay.visibleLabels.length > 0 && (
                            <div className="mb-3 flex items-center">
                              {spendCollateralIconDisplay.visibleLabels.map((label, index) => (
                                <div
                                  key={label}
                                  className="relative h-5 w-5 rounded-full border-2 border-background"
                                  style={{
                                    marginLeft: index > 0 ? '-6px' : '0',
                                    zIndex: spendCollateralIconDisplay.visibleLabels.length - index,
                                  }}
                                >
                                  <Image
                                    src={getTokenImage(TokenSymbol(label))}
                                    alt={label}
                                    width={20}
                                    height={20}
                                    className="rounded-full"
                                  />
                                </div>
                              ))}
                              {spendCollateralIconDisplay.remainingCount > 0 && (
                                <div
                                  className="relative flex h-5 w-5 items-center justify-center rounded-full border-2 border-background bg-muted font-semibold text-[10px]"
                                  style={{ marginLeft: '-6px', zIndex: 0 }}
                                >
                                  +{spendCollateralIconDisplay.remainingCount}
                                </div>
                              )}
                            </div>
                          )}
                          <Typography variant="p" className="text-sm text-yellow-400">
                            {getSpendCollateralEmptyStateMessage(supportedSpendCollateralLabels)}
                          </Typography>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

              {/* Stats row - only show in Borrow mode */}
              {mode === 'borrow' && (
                <div className="flex items-center gap-6">
                  <div>
                    <Typography variant="p" className="text-muted-foreground text-xs">
                      Borrow APY
                    </Typography>
                    <Typography variant="p" className="font-semibold text-lg">
                      {borrowAPY?.toFixed(2)}%
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="p" className="text-muted-foreground text-xs">
                      Health Factor
                    </Typography>
                    <Typography variant="p" className="font-semibold text-lg">
                      {healthFactor}
                    </Typography>
                  </div>
                </div>
              )}

              {/* Delegation warning */}
              {!isCurrentConfigActive && delegation.needsDelegation && selectedMarket !== 'euler' && (
                <DelegationApprovalBox
                  debtTokenAddress={pooledDebtTokenAddress}
                  borrowTokenAddress={borrowTokenAddress}
                />
              )}

              {/* Activate button */}
              <div className="flex flex-col gap-3">
                {selectedMarket === 'euler' && mode === 'borrow' && hasUnsupportedDebt && !isCurrentConfigActive && (
                  <UnsupportedDebtWarning
                    message={`Your current Euler position (sub-account #${cardSubAccountId ?? eulerSubAccountId}) has debt in a vault not supported by the card solver. Please switch to a different position.`}
                  />
                )}
                {selectError && !isCurrentConfigActive && (
                  <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3">
                    <Typography variant="p" className="text-red-400 text-sm">
                      {selectError}
                    </Typography>
                  </div>
                )}
                {isCurrentConfigActive ? (
                  <Button
                    disabled
                    className="w-full border border-primary/20 bg-primary/10 text-primary opacity-100 hover:bg-primary/10"
                    size="lg"
                  >
                    <Check className="mr-2 h-5 w-5" />
                    Currently Active
                  </Button>
                ) : mode === 'spend' && !currentMarket.spendCreditType ? (
                  <Button disabled className="w-full" size="lg" variant="outline">
                    Unavailable for Market
                  </Button>
                ) : (
                  <Button
                    onClick={handleRequestActivate}
                    disabled={
                      creditProviderMutation.isPending ||
                      delegation.isApproving ||
                      delegation.isConfirming ||
                      (selectedMarket === 'euler' && eulerDelegation.isLoadingAuthorization) ||
                      aTokenApproval.isApproving ||
                      aTokenApproval.isConfirming ||
                      hasPooledBorrowEModeMismatch ||
                      (selectedMarket === 'euler' && mode === 'borrow' && hasUnsupportedDebt)
                    }
                    className="w-full bg-gradient-to-r from-gradient-yellow to-gradient-green text-black"
                    size="lg"
                  >
                    {creditProviderMutation.isPending
                      ? 'Updating...'
                      : selectedMarket === 'euler' && eulerDelegation.isLoadingAuthorization
                        ? 'Checking setup...'
                        : delegation.isApproving || delegation.isConfirming
                          ? 'Approving Delegation...'
                          : mode === 'borrow' && delegation.needsDelegation
                            ? 'Approve & Activate'
                            : mode === 'spend' && aTokenApproval.needsApproval && selectedAsset
                              ? 'Approve & Activate'
                              : `Activate ${selectedMarket === 'euler' ? (eulerCluster === 'prime' ? 'Prime' : 'Yield') : currentMarket.displayName} ${mode === 'borrow' ? 'Borrow' : 'Spend'}`}
                  </Button>
                )}
                {pooledBorrowEModeMismatchMessage && (
                  <CardWarningPanel
                    message={pooledBorrowEModeMismatchMessage}
                    inlineActionLabel="Disable eMode"
                    onInlineActionClick={() =>
                      openDialog(eModeDialogConfig, {
                        userEModeCategoryId: pooledBorrowUserEModeCategoryId,
                      })
                    }
                  />
                )}
              </div>
            </Panel.Content>
          </Panel.Wrapper>
        </div>

        {/* ── Right Column: Collateral & Borrow Management ── */}
        <div className="flex flex-col gap-6">
          {mode === 'borrow' && (
            <Panel.Wrapper>
              <Panel.Content className="space-y-6 p-6">
                <div className="rounded-lg border border-border/50 bg-background/30 p-4">
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <Typography variant="p" className="text-muted-foreground text-sm">
                        Amount Borrowed
                      </Typography>
                      <Typography variant="h4" className="font-semibold">
                        ${borrowedUSD.toFixed(2)}
                      </Typography>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleRepay}
                      disabled={
                        borrowedUSD === 0 ||
                        (selectedMarket === 'euler' &&
                          (!eulerData.primaryBorrowPosition || !eulerData.eulerVault?.asset)) ||
                        (selectedMarket === 'hypurrFi' && !hypurrfiData.borrowToken)
                      }
                    >
                      Repay
                    </Button>
                  </div>

                  <BorrowBar positionSummary={positionSummary} />
                  <Ticks ticks={ticks} />
                </div>

                <div>
                  <div className="mb-3 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-1.5">
                        <Typography variant="h4" className="font-semibold">
                          Your Collateral
                        </Typography>
                        {selectedMarket === 'euler' && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className="flex h-4 w-4 items-center justify-center text-muted-foreground/70 hover:text-muted-foreground"
                                aria-label="How collateral is counted"
                              >
                                <Info className="h-3.5 w-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContentShort>
                              Only deposits enabled as collateral on this Euler position contribute to borrow capacity.
                              Savings (deposits not yet enabled) are listed below with an Enable action.
                            </TooltipContentShort>
                          </Tooltip>
                        )}
                      </div>
                      <Typography variant="p" className="text-muted-foreground text-sm">
                        {selectedMarket === 'euler'
                          ? `Total deposited in ${eulerCluster === 'prime' ? 'Prime' : 'Yield'}: $${totalCollateralUSD.toFixed(2)}`
                          : `Total deposited: $${totalCollateralUSD.toFixed(2)}`}
                      </Typography>
                    </div>
                    {(selectedMarket !== 'euler' || !isNewEulerPosition || isActiveEulerVault) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (selectedMarket === 'euler') {
                            openDialog(eAddToPositionDialogConfig as any, {
                              subAccountId: effectiveSubAccountId,
                              controller: borrowVaultAddress ?? null,
                              currentDebtUSD: eulerData.totalDebtValueUSD ?? 0,
                              currentLTV: eulerData.currentLTV ?? 0,
                              liquidationLTV: eulerData.effectiveLiquidationLTV ?? 0,
                            })
                          } else {
                            router.push('/markets/pooled')
                          }
                        }}
                      >
                        <Plus className="mr-1 h-3 w-3" />
                        Add
                      </Button>
                    )}
                  </div>
                  {selectedMarket === 'euler' && eulerData.isLoadingPositionData && !isNewEulerPosition ? (
                    <EulerCollateralBreakdownSkeleton />
                  ) : collateralPositions.length === 0 &&
                    selectedMarket === 'euler' &&
                    isNewEulerPosition &&
                    !isActiveEulerVault ? (
                    <div className="rounded-lg border border-border/50 bg-muted/20 p-6 text-center">
                      <Typography variant="p" className="text-muted-foreground text-sm">
                        Collateral will be available after you activate this position.
                      </Typography>
                    </div>
                  ) : collateralPositions.length === 0 ? (
                    <button
                      type="button"
                      onClick={() => {
                        if (selectedMarket === 'euler') {
                          openDialog(eAddToPositionDialogConfig as any, {
                            subAccountId: effectiveSubAccountId,
                            controller: borrowVaultAddress ?? null,
                            currentDebtUSD: eulerData.totalDebtValueUSD ?? 0,
                            currentLTV: eulerData.currentLTV ?? 0,
                            liquidationLTV: eulerData.effectiveLiquidationLTV ?? 0,
                          })
                        } else {
                          router.push('/markets/pooled')
                        }
                      }}
                      className="flex w-full flex-col items-center justify-center gap-3 rounded-xl border-2 border-primary/30 border-dashed bg-primary/5 p-8 transition-all hover:border-primary/50 hover:bg-primary/10"
                    >
                      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-gradient-yellow/20 to-gradient-green/20">
                        <Plus className="h-7 w-7 text-primary" />
                      </div>
                      <div className="text-center">
                        <Typography variant="p" className="font-medium text-primary">
                          Deposit Collateral
                        </Typography>
                        <Typography variant="p" className="text-muted-foreground text-xs">
                          Add assets to this position to enable spending power
                        </Typography>
                      </div>
                    </button>
                  ) : selectedMarket === 'euler' ? (
                    <EulerCollateralBreakdown
                      enabledCollateral={enabledCollateralPositions}
                      savings={savingsPositions}
                      mode={mode}
                      selectedAsset={selectedAsset as ELendPosition}
                      onAssetSelection={(asset) => setSelectedAsset(asset)}
                      onEnableAsCollateral={handleEnableAsCollateral}
                      enableDisabledByVault={enableDisabledByVault}
                      controllerMarketName={controllerMarketName}
                    />
                  ) : (
                    <CollateralAssetsSection
                      assets={collateralPositions as UserPosition[]}
                      totalCollateral={totalCollateralUSD}
                      mode={mode}
                      showRadioSelection={false}
                      selectedAsset={selectedAsset as UserPosition}
                      onAssetSelection={setSelectedAsset}
                      protocol={selectedMarket}
                    />
                  )}
                </div>
              </Panel.Content>
            </Panel.Wrapper>
          )}

          {mode === 'spend' && selectedAsset && (
            <Panel.Wrapper>
              <Panel.Content className="space-y-6 p-6">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-gradient-yellow/20 to-gradient-green/20">
                    <TrendingUp className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <Typography variant="h4" className="font-semibold">
                      Spend Summary
                    </Typography>
                    <Typography variant="p" className="text-muted-foreground text-sm">
                      Selected collateral for spending
                    </Typography>
                  </div>
                </div>

                <div className="rounded-lg border border-border/50 bg-background/30 p-4">
                  <div className="flex items-center gap-3">
                    <Image
                      src={getTokenImage(
                        selectedMarket === 'euler'
                          ? TokenSymbol((selectedAsset as ELendPosition).token.symbol)
                          : (selectedAsset as UserPosition).reserve.token.symbol,
                      )}
                      alt={
                        selectedMarket === 'euler'
                          ? (selectedAsset as ELendPosition).token.symbol
                          : (selectedAsset as UserPosition).reserve.token.symbol
                      }
                      width={40}
                      height={40}
                      className="rounded-full"
                    />
                    <div className="flex-1">
                      <Typography variant="p" className="font-semibold">
                        {selectedMarket === 'euler'
                          ? (selectedAsset as ELendPosition).token.symbol
                          : (selectedAsset as UserPosition).reserve.token.symbol}
                      </Typography>
                      <Typography variant="p" className="text-muted-foreground text-sm">
                        {selectedMarket === 'euler'
                          ? (selectedAsset as ELendPosition).vaultName
                          : 'Available Collateral'}
                      </Typography>
                    </div>
                    <div className="text-right">
                      <Typography variant="p" className="text-muted-foreground text-xs">
                        Spending Power
                      </Typography>
                      <Typography variant="p" className="font-semibold">
                        $
                        {selectedMarket === 'euler'
                          ? (selectedAsset as any).spendingPowerUSD?.toFixed(2) ??
                            (selectedAsset as ELendPosition).depositAmountUSD.toFixed(2)
                          : (selectedAsset as any).spendingPowerUSD?.toFixed(2) ??
                            (selectedAsset as UserPosition).collateralBalance
                              .multipliedBy((selectedAsset as UserPosition).reserve.priceInUSD)
                              .toFixed(2)}
                      </Typography>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                  <Typography variant="p" className="text-primary text-sm">
                    This collateral will be used as purchasing power when you spend with your card.
                  </Typography>
                </div>
              </Panel.Content>
            </Panel.Wrapper>
          )}
        </div>
      </div>

      <ActivateConfirmationModal
        open={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false)
          setActivatedEulerSubAccountId(null)
        }}
        onConfirm={handleActivate}
        isLoading={creditProviderMutation.isPending || delegation.isApproving || delegation.isConfirming}
        from={currentConfigSummary}
        to={newConfigSummary}
        isNewPosition={selectedMarket === 'euler' && isNewEulerPosition && mode !== 'spend'}
        newPositionSubAccountId={isNewEulerPosition ? (activatedEulerSubAccountId ?? nextEulerSubAccountId) : undefined}
        needsDelegation={selectedMarket === 'euler' ? eulerDelegation.needsDelegation : delegation.needsDelegation}
        eulerCluster={eulerCluster}
        onDepositCollateral={() => {
          if (selectedMarket === 'euler') {
            openDialog(eAddToPositionDialogConfig as any, {
              subAccountId: activatedEulerSubAccountId ?? nextEulerSubAccountId,
              controller: borrowVaultAddress ?? null,
              currentDebtUSD: eulerData.totalDebtValueUSD ?? 0,
              currentLTV: eulerData.currentLTV ?? 0,
              liquidationLTV: eulerData.effectiveLiquidationLTV ?? 0,
            })
          } else {
            router.push('/markets/pooled')
          }
        }}
      />
    </div>
  )
}

function EulerCollateralBreakdown({
  enabledCollateral,
  savings,
  mode,
  selectedAsset,
  onAssetSelection,
  onEnableAsCollateral,
  enableDisabledByVault,
  controllerMarketName,
}: {
  enabledCollateral: ELendPosition[]
  savings: ELendPosition[]
  mode: 'borrow' | 'spend'
  selectedAsset?: ELendPosition | null
  onAssetSelection: (asset: ELendPosition | null) => void
  onEnableAsCollateral: (position: ELendPosition) => void
  /** Map<vaultAddress.toLowerCase(), reason>: when present, the Enable button is shown as disabled. */
  enableDisabledByVault?: Map<string, string>
  /** Market name of the card's borrow vault (e.g. "HypurrFi Prime"), used in explanatory copy. */
  controllerMarketName?: string
}) {
  const savingsTotalUSD = useMemo(() => savings.reduce((sum, pos) => sum + Number(pos.depositAmountUSD), 0), [savings])
  const hasSavings = savings.length > 0
  const hasEnabled = enabledCollateral.length > 0
  const hasCrossMarketSavings = useMemo(() => {
    if (!enableDisabledByVault || enableDisabledByVault.size === 0) return false
    return savings.some((pos) => enableDisabledByVault.has(pos.vaultAddress.toLowerCase()))
  }, [savings, enableDisabledByVault])

  return (
    <div className="flex flex-col">
      {hasSavings && !hasEnabled && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />
          <Typography variant="p" className="text-amber-300 text-xs leading-snug">
            You&apos;ve deposited ${savingsTotalUSD.toFixed(2)} in this position as savings, but none of it is enabled
            as collateral yet. Enable a vault below to unlock spending power for this card.
          </Typography>
        </div>
      )}

      <BreakdownSection label="Enabled Collateral" hint="Counts toward spending power">
        {hasEnabled ? (
          <EulerCollateralSection
            assets={enabledCollateral}
            mode={mode}
            showRadioSelection={false}
            selectedAsset={selectedAsset}
            onAssetSelection={onAssetSelection}
            showTotals={false}
          />
        ) : (
          <div className="rounded-md border border-border/50 bg-muted/10 px-3 py-4 text-center">
            <Typography variant="p" className="text-muted-foreground text-xs">
              No deposits are enabled as collateral for this position yet.
            </Typography>
          </div>
        )}
      </BreakdownSection>

      {hasSavings && (
        <>
          <div className="my-4 border-border/50 border-t" />
          <BreakdownSection label="Savings in this Position" hint="Deposited but not used as collateral">
            {hasCrossMarketSavings && controllerMarketName && (
              <div className="mb-2 flex items-start gap-2 rounded-md border border-border/50 bg-muted/20 px-3 py-2">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <Typography variant="p" className="text-muted-foreground text-xs leading-snug">
                  Deposits in a different market than {controllerMarketName} cannot be enabled here - enabling them
                  on-chain wouldn&apos;t back this card&apos;s borrow vault. Move funds into a {controllerMarketName}{' '}
                  vault (or switch the card&apos;s market) to use them as collateral.
                </Typography>
              </div>
            )}
            <EulerCollateralSection
              assets={savings}
              mode={mode}
              showRadioSelection={false}
              selectedAsset={selectedAsset}
              onAssetSelection={onAssetSelection}
              showTotals={false}
              rowAction={{
                header: '',
                render: (position) => {
                  const crossMarketReason = enableDisabledByVault?.get(position.vaultAddress.toLowerCase())
                  const isDisabled = !!crossMarketReason
                  return (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isDisabled}
                          onClick={(e) => {
                            e.stopPropagation()
                            if (isDisabled) return
                            onEnableAsCollateral(position)
                          }}
                          className={cn(
                            'h-7 whitespace-nowrap px-2.5 text-xs',
                            isDisabled
                              ? 'cursor-not-allowed border-border/40 text-muted-foreground/60'
                              : 'border-primary/40 text-primary hover:bg-primary/10',
                          )}
                        >
                          Enable
                        </Button>
                      </TooltipTrigger>
                      <TooltipContentShort>
                        {crossMarketReason ?? 'Enable this deposit as collateral to unlock borrow capacity.'}
                      </TooltipContentShort>
                    </Tooltip>
                  )
                },
              }}
            />
          </BreakdownSection>
        </>
      )}
    </div>
  )
}

function BreakdownSection({ label, hint, children }: { label: string; hint: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
        <Typography variant="p" className="font-semibold text-foreground text-sm leading-tight">
          {label}
        </Typography>
        <Typography variant="p" className="text-[11px] text-muted-foreground leading-tight">
          {hint}
        </Typography>
      </div>
      {children}
    </div>
  )
}

function ModeTab({
  label,
  active,
  isLive,
  disabled,
  onClick,
}: {
  label: string
  active: boolean
  isLive?: boolean
  disabled?: boolean
  onClick: () => void
}) {
  const button = (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'relative rounded-lg border px-4 py-2 font-medium text-sm transition-all',
        active
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      {label}
      {isLive && (
        <span className="-top-1 -right-1 absolute flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
        </span>
      )}
    </button>
  )

  if (isLive) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContentShort>Currently active</TooltipContentShort>
      </Tooltip>
    )
  }

  return button
}

function Ticks({ ticks }: { ticks: { x: number; label?: string }[] }) {
  return (
    <div className="relative h-8">
      {ticks.map(({ label, x }) => (
        <div className="-translate-x-[50%] absolute flex flex-col items-center" style={{ left: `${x}%` }} key={x}>
          <div className="h-2 w-[1px] bg-icon-foreground" />
          <div>{label && <Typography variant="prompt">{label}</Typography>}</div>
        </div>
      ))}
    </div>
  )
}

function EulerPositionCard({
  position,
  isSelected,
  isActivePosition,
  fallbackBorrowVaultAddress,
  userAddress,
  chainId,
  onClick,
  onAddCollateral,
}: {
  position: MergedPosition
  isSelected: boolean
  isActivePosition: boolean
  fallbackBorrowVaultAddress: Address | undefined
  userAddress: Address | undefined
  chainId: number
  onClick: () => void
  onAddCollateral: (liquidity: { currentDebtUSD: number; currentLTV: number; liquidationLTV: number }) => void
}) {
  const { resolveMarket } = useVaultClassifier()
  const liabilityVaultAddress = useMemo(() => {
    if (position.debtVaults.length > 0) {
      return position.debtVaults[0]!.liabilityVaultAddress as Address
    }
    return fallbackBorrowVaultAddress
  }, [position.debtVaults, fallbackBorrowVaultAddress])

  const subAccountAddress = useMemo(() => {
    if (!userAddress) return undefined
    return getSubAccountAddress(userAddress, position.subAccountId)
  }, [userAddress, position.subAccountId])

  const { data: liquidityData, isLoading: isLiquidityLoading } = useAccountLiquidity({
    accountAddress: subAccountAddress,
    liabilityVault: liabilityVaultAddress,
    chainId,
    enabled: !!subAccountAddress && !!liabilityVaultAddress,
  })

  // Query the EVC directly for enabled collaterals - matches the dashboard's
  // Sub-account Positions view and includes collaterals whose LTV isn't
  // configured against the currently-selected liability vault.
  const { enabledVaults, isLoading: isLoadingEnabled } = useEnabledCollateralVaults({
    accountAddress: subAccountAddress,
    chainId,
    enabled: !!subAccountAddress,
  })

  // Same market-match rule the dashboard uses: a deposit only counts as
  // Collateral when it's enabled AND its market matches the controller's
  // market. Enabled-but-different-market deposits are treated as savings.
  // When the position has no debt yet, fall back to the target borrow vault's
  // market so the classification matches what the card will actually use.
  const controllerMarketKey = useMemo(() => {
    if (position.debtVaults.length > 0) {
      return resolveMarket(position.debtVaults[0]!.liabilityVaultAddress as Address)?.key
    }
    if (fallbackBorrowVaultAddress) {
      return resolveMarket(fallbackBorrowVaultAddress)?.key
    }
    return undefined
  }, [position.debtVaults, fallbackBorrowVaultAddress, resolveMarket])

  const { enabledCollateralUSD, savingsUSD, eligibleCollateralItems } = useMemo(() => {
    const enabledSet = new Set(enabledVaults)
    let enabled = 0
    let savings = 0
    const items: { vaultAddress: string; depositAmountUSD: number }[] = []
    for (const c of position.collateral) {
      const isEnabled = enabledSet.has(c.vaultAddress.toLowerCase())
      if (!isEnabled) {
        savings += c.depositAmountUSD
        continue
      }
      if (!controllerMarketKey) {
        enabled += c.depositAmountUSD
        items.push({ vaultAddress: c.vaultAddress, depositAmountUSD: c.depositAmountUSD })
        continue
      }
      const vaultMarketKey = resolveMarket(c.vaultAddress as Address)?.key
      if (vaultMarketKey === controllerMarketKey) {
        enabled += c.depositAmountUSD
        items.push({ vaultAddress: c.vaultAddress, depositAmountUSD: c.depositAmountUSD })
      } else {
        savings += c.depositAmountUSD
      }
    }
    return { enabledCollateralUSD: enabled, savingsUSD: savings, eligibleCollateralItems: items }
  }, [enabledVaults, position.collateral, controllerMarketKey, resolveMarket])

  // Fallback LTV-weighted sum used when the Account Lens has no data for this
  // sub-account (e.g. no controller is enabled yet, so `getAccountInfo`
  // returns queryFailure). Only counts deposits that are enabled as collateral
  // AND belong to the same market as the target borrow vault - preserving the
  // "only enabled collateral counts" rule.
  const ltvFallback = useLtvWeightedCollateral({
    borrowVaultAddress: liabilityVaultAddress,
    collateral: eligibleCollateralItems,
    chainId,
  })

  const hasAnyDeposit = position.collateral.length > 0
  const hasEnabledCollateral = enabledCollateralUSD > 0
  const hasSavings = savingsUSD > 0
  const savingsOnly = hasAnyDeposit && !hasEnabledCollateral && hasSavings

  // Spending power prefers the lens result (handles debt + HF correctly).
  // Fallback chain:
  //   1. Lens data → LTV-weighted max borrow - current debt
  //   2. No enabled+same-market deposits → $0 (nothing to borrow against)
  //   3. No liability vault at all → raw enabled-collateral USD (user hasn't
  //      picked a borrow vault yet, so we can't LTV-weight; show the cap)
  //   4. LTVFull reads pending → undefined (show loading)
  //   5. LTV reads done → LTV-weighted fallback
  const spendingPower = useMemo(() => {
    if (liquidityData != null) {
      return Math.max(liquidityData.maxBorrowCapacityUSD - (liquidityData.totalDebtBorrowUSD ?? 0), 0)
    }
    if (isLiquidityLoading || isLoadingEnabled) return undefined
    if (eligibleCollateralItems.length === 0) return 0
    if (!liabilityVaultAddress) return enabledCollateralUSD
    if (ltvFallback === undefined) return undefined
    return ltvFallback
  }, [
    liquidityData,
    isLiquidityLoading,
    isLoadingEnabled,
    eligibleCollateralItems,
    liabilityVaultAddress,
    enabledCollateralUSD,
    ltvFallback,
  ])

  const formatUSD = (value: number) =>
    `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group relative flex w-full flex-col gap-2 rounded-xl border p-4 text-left transition-all duration-200',
        isSelected
          ? 'border-primary/50 bg-primary/5 shadow-sm ring-1 ring-primary/20'
          : 'border-border/50 bg-secondary/20 hover:border-primary/30 hover:bg-secondary/40',
      )}
    >
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-2">
          {position.debtVaults[0] && (
            <Image
              src={getTokenImage(TokenSymbol(position.debtVaults[0].stableSymbol))}
              alt={position.debtVaults[0].stableSymbol}
              width={22}
              height={22}
              className="rounded-full"
            />
          )}
          <div className="flex flex-col">
            <div className="flex items-center gap-1.5">
              <Typography variant="p" className="font-semibold text-foreground text-sm leading-tight tracking-tight">
                {position.debtVaults[0]?.stableSymbol ?? `Position ${position.subAccountId}`}
              </Typography>
              {isActivePosition && (
                <div className="flex items-center gap-1 rounded-full bg-green-500/10 px-1.5 py-0.5 font-semibold text-[9px] text-green-400 uppercase tracking-wide">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-500" />
                  </span>
                  Active
                </div>
              )}
              {savingsOnly && !isActivePosition && !isLoadingEnabled && !isLiquidityLoading && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex items-center gap-1 rounded-full bg-amber-500/10 px-1.5 py-0.5 font-semibold text-[9px] text-amber-400 uppercase tracking-wide">
                      Savings only
                    </span>
                  </TooltipTrigger>
                  <TooltipContentShort>
                    You&apos;ve deposited here but no vault is enabled as collateral. Select this position and use
                    Enable as Collateral to unlock spending power.
                  </TooltipContentShort>
                </Tooltip>
              )}
            </div>
            <span className="text-muted-foreground text-xs leading-tight">Position {position.subAccountId}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasAnyDeposit ? (
            <div className="text-right">
              <div className="text-muted-foreground text-xs">Spending Power</div>
              <div className="font-semibold text-foreground text-sm">
                {isLiquidityLoading || isLoadingEnabled || spendingPower === undefined ? (
                  <span className="animate-pulse text-muted-foreground">...</span>
                ) : (
                  formatUSD(spendingPower)
                )}
              </div>
              {hasEnabledCollateral && hasSavings && (
                <div className="text-[10px] text-muted-foreground/70 leading-tight">
                  + {formatUSD(savingsUSD)} in savings
                </div>
              )}
              {savingsOnly && !isLiquidityLoading && !isLoadingEnabled && (
                <div className="text-[10px] text-amber-400/80 leading-tight">{formatUSD(savingsUSD)} not enabled</div>
              )}
            </div>
          ) : (
            <div
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation()
                onAddCollateral({
                  currentDebtUSD: liquidityData?.totalDebtValueUSD ?? 0,
                  currentLTV: liquidityData?.currentLTV ?? 0,
                  liquidationLTV: liquidityData?.effectiveLiquidationLTV ?? 0,
                })
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.stopPropagation()
                  onAddCollateral({
                    currentDebtUSD: liquidityData?.totalDebtValueUSD ?? 0,
                    currentLTV: liquidityData?.currentLTV ?? 0,
                    liquidationLTV: liquidityData?.effectiveLiquidationLTV ?? 0,
                  })
                }
              }}
              className="flex cursor-pointer items-center gap-1.5 font-medium text-xs text-yellow-400 hover:text-yellow-300"
            >
              <Plus className="h-3 w-3" />
              Add Collateral
            </div>
          )}
          {isSelected && (
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm">
              <Check className="h-3 w-3 stroke-[3]" />
            </div>
          )}
        </div>
      </div>

      {savingsOnly && !isLoadingEnabled && !isLiquidityLoading && (
        <div className="mt-1 flex items-center gap-2 rounded-md border border-amber-500/20 bg-amber-500/10 px-3 py-2">
          <Info className="h-3.5 w-3.5 shrink-0 text-amber-400" />
          <Typography variant="p" className="font-medium text-amber-300 text-xs">
            Select this position to enable your deposits as collateral and unlock spending power.
          </Typography>
        </div>
      )}

      {position.hasUnsupportedDebt && (
        <div className="mt-1 rounded-md border border-yellow-500/20 bg-yellow-500/10 px-3 py-2">
          <Typography variant="p" className="font-medium text-xs text-yellow-500">
            Has unsupported debt vault
          </Typography>
        </div>
      )}
    </button>
  )
}

function BorrowBar({ positionSummary }: { positionSummary?: PositionSummary }) {
  if (!positionSummary?.hasCollaterals || positionSummary.borrow.max.eq(0)) {
    return (
      <div className="relative flex h-4 flex-row">
        <div className="w-full rounded-lg border bg-black/10" />
      </div>
    )
  }

  const { borrow } = positionSummary

  return (
    <div className="relative flex h-4 flex-row">
      {borrow.percents.borrowed !== 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className="z-[1] rounded-lg bg-gradient-to-r from-gradient-yellow to-gradient-green"
              style={{ width: `${borrow.percents.borrowed}%` }}
            />
          </TooltipTrigger>
          <TooltipContentShort>You have borrowed ${positionSummary.borrow.current.toFixed(2)}.</TooltipContentShort>
        </Tooltip>
      )}
      {borrow.percents.rest !== 0 && (
        <div
          className="rounded-lg border bg-muted"
          style={{
            width: borrow.percents.rest === 100 ? '100%' : `calc(${borrow.percents.rest}% + 1rem)`,
            marginLeft: borrow.percents.rest === 100 ? '0' : '-1rem',
          }}
        />
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="-translate-x-[1px] -translate-y-1/2 absolute top-1/2 z-[2] h-7 border-primary border-l-2 pl-2"
            style={{ left: `${borrow.percents.max}%` }}
          >
            <div className="-mt-[.5px] flex min-h-full flex-col justify-center text-sm">max</div>
          </div>
        </TooltipTrigger>
        <TooltipContentShort>You can borrow up to ${positionSummary.borrow.max.toFixed(2)}.</TooltipContentShort>
      </Tooltip>
    </div>
  )
}
